import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { decodeAccessToken, logout } from '../utils/auth';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const payload = decodeAccessToken();
    if (payload) {
      setIsAdmin(payload.admin === true);
      setUserEmail(payload.email || '');
    }
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const NavButton = ({ path, label, accent }: { path: string; label: string; accent?: 'green' | 'purple' }) => {
    const active = isActive(path);
    let classes = 'nav-link';

    if (active) {
      classes = accent === 'green'
        ? 'nav-link bg-emerald-500/10 text-emerald-400'
        : accent === 'purple'
        ? 'nav-link bg-purple-500/10 text-purple-400'
        : 'nav-link active';
    } else if (accent === 'green') {
      classes = 'nav-link text-emerald-400 hover:bg-emerald-500/10';
    } else if (accent === 'purple') {
      classes = 'nav-link text-purple-400 hover:bg-purple-500/10';
    }

    return (
      <button onClick={() => navigate(path)} className={classes}>
        {label}
      </button>
    );
  };

  return (
    <nav className="nav-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center space-x-8">
            {/* Logo/Brand */}
            <div className="flex items-center">
              <span className="text-xl font-semibold text-amber-400">
                AdjusterPhoto
              </span>
            </div>

            {/* Main Navigation - Desktop */}
            <div className="hidden md:flex items-center space-x-2">
              <NavButton path="/uploads" label="My Uploads" />
              <NavButton path="/upload-image" label="Upload Image" />
              <NavButton path="/download" label="Download App" accent="green" />
              <NavButton path="/pricing" label="Pricing" />
              <NavButton path="/billing" label="Billing" />
              {isAdmin && (
                <NavButton path="/admin" label="Admin Console" accent="purple" />
              )}
            </div>
          </div>

          {/* Right side - User menu */}
          <div className="hidden md:flex items-center space-x-4">
            {userEmail && (
              <span className="text-sm text-zinc-500">
                {userEmail}
              </span>
            )}

            <button
              onClick={() => navigate('/change-password')}
              className="text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
            >
              Change Password
            </button>

            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-500/90 hover:bg-rose-500 text-white transition-all hover:shadow-lg hover:shadow-rose-500/25"
            >
              Logout
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-zinc-400 hover:text-zinc-200 p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-1">
            <button
              onClick={() => { navigate('/uploads'); setMobileMenuOpen(false); }}
              className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                isActive('/uploads') ? 'bg-amber-500/10 text-amber-400' : 'text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              My Uploads
            </button>
            <button
              onClick={() => { navigate('/upload-image'); setMobileMenuOpen(false); }}
              className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                isActive('/upload-image') ? 'bg-amber-500/10 text-amber-400' : 'text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              Upload Image
            </button>
            <button
              onClick={() => { navigate('/download'); setMobileMenuOpen(false); }}
              className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-emerald-400 hover:bg-emerald-500/10"
            >
              Download App
            </button>
            <button
              onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}
              className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                isActive('/pricing') ? 'bg-amber-500/10 text-amber-400' : 'text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              Pricing
            </button>
            <button
              onClick={() => { navigate('/billing'); setMobileMenuOpen(false); }}
              className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                isActive('/billing') ? 'bg-amber-500/10 text-amber-400' : 'text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              Billing
            </button>
            {isAdmin && (
              <button
                onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-purple-400 hover:bg-purple-500/10"
              >
                Admin Console
              </button>
            )}

            <div className="border-t border-zinc-800 mt-2 pt-2">
              {userEmail && (
                <p className="px-3 py-2 text-sm text-zinc-500">{userEmail}</p>
              )}
              <button
                onClick={() => { navigate('/change-password'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-zinc-400 hover:bg-zinc-800"
              >
                Change Password
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-rose-400 hover:bg-rose-500/10"
              >
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
